import type { TFunction } from "i18next";
import { confidenceFromSortScore } from "@/lib/elementLabel";
import { elementLabel } from "@/lib/elementLabels";
import type { NeedsYouItem, NeedsYouUpdate } from "@/types/needsYou";

/**
 * The words for one Needs-you row (§ 12 § 3.4 item 2). The server sends a
 * structured item (a type + its parameters); every visible string comes from
 * the catalogs here, so a German workspace never reads English the server
 * composed. Element keys go through the translated element helper; the next
 * bet's strength through the evidence-strength tier words; an age through
 * Intl.RelativeTimeFormat in the interface language.
 */
export interface NeedsYouRowCopy {
  /** The row's kind word ("Blocked", "Review", "Winner", "Next bet", "Setup"). */
  tag: string;
  tone: "error" | "warning" | "info" | "default";
  title: string;
  detail: string;
  actionLabel: string;
  /** The row's subject, for the action's accessible name. */
  subject: string;
}

/** The session row's title, by the connector state the server sent (shared
 *  with the experiment Detail's store line -- C10 M3-R05-3). */
export function sessionStateCopyKey(
  state: string | null | undefined,
): "expired" | "wrongAccount" | "notWorking" | "notConnected" {
  if (state === "auth_failed") return "expired";
  if (state === "wrong_account") return "wrongAccount";
  if (!state || state === "never_connected") return "notConnected";
  return "notWorking";
}

/** The store product the session belongs to -- "App Store Connect" /
 *  "Google Play Console" -- the ONE name the Needs-you row, the bell and
 *  the off-Dashboard upper notice all use (design spec section 12 Rule 9;
 *  ENG-APP-UX-TUNEUP C9 W5). */
export function consoleName(
  t: TFunction,
  platform: NeedsYouItem["platform"],
): string {
  return platform === "android"
    ? t("ui.credentialHealth.owner.googlePlayConsole")
    : t("ui.credentialHealth.owner.appStoreConnect");
}

/**
 * "3 days ago" in the interface language (Intl.RelativeTimeFormat), never
 * the English `formatRelative` helper. A future or unreadable timestamp
 * reads as "now" rather than a negative age.
 */
export function relativeAge(iso: string, lang: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const minutes = Math.max(0, Math.floor((Date.now() - then) / 60_000));
  let formatter: Intl.RelativeTimeFormat;
  try {
    formatter = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });
  } catch {
    formatter = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  }
  if (minutes < 60) return formatter.format(-minutes, "minute");
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return formatter.format(-hours, "hour");
  const days = Math.floor(hours / 24);
  if (days < 30) return formatter.format(-days, "day");
  return formatter.format(-Math.floor(days / 30), "month");
}

/**
 * A test is named by its explicit name, else by "element — direction" (the
 * synthesized title Active and History use), else by whichever exists;
 * never by an id.
 */
function testName(t: TFunction, item: NeedsYouItem): string {
  if (item.experiment_name) return item.experiment_name;
  const element = item.element ? elementLabel(t, item.element) : "";
  if (element && item.direction) return `${element} — ${item.direction}`;
  return item.direction || element || t("dashboard.needsYou.unnamedTest");
}

export function needsYouRowCopy(
  t: TFunction,
  item: NeedsYouItem,
  lang = "en",
): NeedsYouRowCopy {
  const element = item.element ? elementLabel(t, item.element) : "";
  const name = testName(t, item);
  switch (item.type) {
    case "store_session": {
      const consoleLabel = consoleName(t, item.platform);
      const state = sessionStateCopyKey(item.session_state);
      const lastWorked = item.last_good_at
        ? t("dashboard.needsYou.storeSession.lastWorked", {
            relative: relativeAge(item.last_good_at, lang),
          })
        : "";
      return {
        tag: t("dashboard.needsYou.tag.blocked"),
        tone: "error",
        title: t(`dashboard.needsYou.storeSession.title.${state}`, {
          console: consoleLabel,
        }),
        detail: [t("dashboard.needsYou.storeSession.detail"), lastWorked]
          .filter(Boolean)
          .join(" "),
        actionLabel:
          state === "notConnected"
            ? t("dashboard.needsYou.storeSession.connect", {
                console: consoleLabel,
              })
            : t("dashboard.needsYou.storeSession.reconnect", {
                console: consoleLabel,
              }),
        subject: consoleLabel,
      };
    }
    case "review_result":
      return {
        tag: t("dashboard.needsYou.tag.review"),
        tone: "warning",
        title: t("dashboard.needsYou.review.title", { name }),
        detail: element
          ? t("dashboard.needsYou.review.detailWithElement", { element })
          : t("dashboard.needsYou.review.detail"),
        actionLabel: t("dashboard.needsYou.review.action"),
        subject: name,
      };
    case "apply_winner":
      return {
        tag: t("dashboard.needsYou.tag.winner"),
        tone: "info",
        title: t("dashboard.needsYou.winner.title", { name }),
        detail: element
          ? t("dashboard.needsYou.winner.detailWithElement", { element })
          : t("dashboard.needsYou.winner.detail"),
        actionLabel: t("dashboard.needsYou.winner.action"),
        subject: name,
      };
    case "next_bets": {
      const level = confidenceFromSortScore(item.confidence ?? 0);
      const count = item.count ?? 1;
      return {
        tag: t("dashboard.needsYou.tag.nextBet"),
        tone: "default",
        title:
          item.proposition ?? t("dashboard.needsYou.nextBets.fallbackTitle"),
        detail: t("dashboard.needsYou.nextBets.detail", {
          tier: t(`ui.evidenceStrength.${level}`),
          count,
        }),
        actionLabel: t("dashboard.needsYou.nextBets.action", { count }),
        subject: t("dashboard.needsYou.nextBets.subject"),
      };
    }
    case "setup_no_competitors":
      return {
        tag: t("dashboard.needsYou.tag.setup"),
        tone: "default",
        title: t("dashboard.needsYou.noCompetitors.title"),
        detail: t("dashboard.needsYou.noCompetitors.detail"),
        actionLabel: t("dashboard.needsYou.noCompetitors.action"),
        subject: t("dashboard.needsYou.noCompetitors.title"),
      };
    case "setup_incomplete_context":
    default:
      return {
        tag: t("dashboard.needsYou.tag.setup"),
        tone: "default",
        title: t("dashboard.needsYou.incompleteContext.title"),
        detail: t("dashboard.needsYou.incompleteContext.detail"),
        actionLabel: t("dashboard.needsYou.incompleteContext.action"),
        subject: t("dashboard.needsYou.incompleteContext.title"),
      };
  }
}

/** The words for one Updates news line (the bell's Updates tab). */
export function needsYouUpdateCopy(
  t: TFunction,
  update: NeedsYouUpdate,
): { title: string; actionLabel: string } {
  return update.type === "new_signals"
    ? {
        title: t("layout.bell.newSignals", { count: update.count }),
        actionLabel: t("layout.bell.viewSignals"),
      }
    : {
        title: t("layout.bell.competitorUpdates", { count: update.count }),
        actionLabel: t("layout.bell.viewChanges"),
      };
}
